// web43_gym_buddy_clarity (Narek, Sep 25 2026 — the doc calls it "Web42",
// but web42 is the post-purchase protein upsell). Goal: fewer accidental
// two-person plans, fewer refunds.
//
// Variation:
//   1. Entry point: "Add a second person to your plan?" — framed as adding a
//      person to a plan priced for two, with "Yes, add a second person" and
//      "No thanks, just me" as equal-weight buttons (control: "Continue with
//      Gym Buddy" + a small Skip link).
//   2. Opting in skips the guest-pass screen (control shows it after both
//      choices; the variation still shows it after "No thanks").
//   3. Pricing page: the two-person total per month instead of the per-person
//      price, "vs … for a 1-person plan", and a "Switch to 1-person plan"
//      button right under the price (see PricingPlans / PricingPlansWeb21).
// Both arms keep the existing GymBuddyPlanAccepted / GymBuddyPlanSkipped
// events; exposure ExperimentStarted{web43_gym_buddy_clarity} is logged when
// the entry page renders (App.tsx).
import React from "react";
import ReactDOMServer from "react-dom/server";

export const WEB43_ARM_VARIATION = "variation";
export const GYM_BUDDY_ENTRY_PAGE = "togetherConsistent";
export const GUEST_PASS_PAGE = "guestPassInvites";
export const PAGE_AFTER_GUEST_PASS = "allset";

// The survey value is the string "true" from the html element's default and
// a boolean from the footer's nextButtonClickValue; treat both as opted in.
export const isGymBuddyOptedIn = (value: unknown): boolean =>
  value === true || value === "true";

const checkItems = [
  "Two private accounts, two personalized programs",
  "One bill, paid by you",
  "Save up to 15% vs two separate plans",
];

const addSecondPerson = (
  <div className="new-quote-container centered">
    <div className="splash-icon">🤝</div>
    <div className="splash-header">
      <h1 className="no-word-break">Add a second person<br />to your plan?</h1>
      <div className="quote">
        Train with a friend or partner. One subscription covers both of you, priced for two people.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: "400px", margin: "28px auto 0", padding: "0 36px" }}>
        {checkItems.map((item, idx) => (
          <div key={idx} style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "16px", color: "#1a1a1a", textAlign: "left" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", borderRadius: "50%", backgroundColor: "#E5EEFB", color: "#2F6FED", fontSize: "16px", fontWeight: 700, flexShrink: 0 }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z" fill="#E7F0FF" />
                <path d="M14.6665 6.50049L8.2498 12.9172L5.33313 10.0005" stroke="#0B6DFF" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span>{item}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const addSecondPersonElement = {
  name: GYM_BUDDY_ENTRY_PAGE,
  type: "html",
  html: ReactDOMServer.renderToStaticMarkup(addSecondPerson),
  defaultValue: "true",
};

// Variation "You're all set!" screen: the opt-in path never sends an invite,
// so the "Invite on the way." sentence goes (Narek, Sep 27 2026).
export const ALL_SET_PAGE = "allset";
const allSetNoInvite = (
  <div className="new-quote-container centered">
    <div className="splash-icon">🎉</div>
    <div className="splash-header">
      <h1 className="no-word-break">You're all set!</h1>
      <div className="quote">You've just made training more fun-and more consistent.</div>
    </div>
  </div>
);
export const allSetNoInviteElement = {
  name: "training-circle",
  type: "html",
  html: ReactDOMServer.renderToStaticMarkup(allSetNoInvite),
};

export const GYM_BUDDY_CLARITY_PAGE_PROPS = {
  showHeader: false,
  showCurrentSection: false,
  nextButtonText: "Yes, add a second person",
  nextButtonClickEvent: "GymBuddyPlanAccepted",
  nextButtonClickValue: true,
  nextButtonHideArrow: true,
  secondaryButtonText: "No thanks, just me",
  secondaryButtonClickEvent: "GymBuddyPlanSkipped",
  secondaryButtonClickValue: false,
  secondaryButtonTargetPage: GUEST_PASS_PAGE,
};

/** Variation: swap the entry page's content and buttons; every other page is untouched. */
export function buildGymBuddyClarityPages<T extends { name: string }>(
  pages: readonly T[],
  enabled: boolean
): readonly T[] {
  if (!enabled) return pages;
  const i = pages.findIndex((p) => p.name === GYM_BUDDY_ENTRY_PAGE);
  if (i === -1) return pages;
  const next = pages.slice() as T[];
  next[i] = {
    ...(pages[i] as any),
    elements: [addSecondPersonElement],
    props: GYM_BUDDY_CLARITY_PAGE_PROPS,
    noAutoAdvance: true,
  } as T;
  const j = pages.findIndex((p) => p.name === ALL_SET_PAGE);
  if (j !== -1) {
    next[j] = { ...(pages[j] as any), elements: [allSetNoInviteElement] } as T;
  }
  return next;
}

/** "$13.38" per person → "$26.76" for the two-person total. */
export function monthlyForTwo(perPerson: string): string {
  const n = parseFloat(perPerson.replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? `$${(n * 2).toFixed(2)}` : perPerson;
}

/** "vs. $19.99/mo each on separate plans" → "vs. $19.99/mo for a 1-person plan". */
export function onePersonComparison(text: string): string {
  return text.replace(/\s*each on separate plans\.?$/i, " for a 1-person plan");
}
